import {
  DownloadIcon,
  LoadingSkeleton,
  Typography,
} from '@bloomfieldrobotics/canopy';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { useState } from 'react';
import { useVarietiesByCustomer } from '../../../../services/entities/hooks/useVarietiesByCustomer';
import { useTranslation } from '../../../../services/translation/hooks/useTranslation';
import { createExportFileName } from '../../../dataGridTables/utils/createExportFileName';
import { useDownloadCsv } from '../../../duckDB/hooks/useDownloadCsv';
import { createInlineMappingSubquery } from '../../../duckDB/utils/createInlineMappingSubquery';
import { formattedDateColumn } from '../../../duckDB/utils/formattedDateColumn';

export const DownloadClusterReportButton = ({
  customerId,
  shouldUpdate,
  loading,
}: {
  customerId: string;
  shouldUpdate: boolean;
  loading: boolean;
}) => {
  const { t } = useTranslation();
  const { varieties, isVarietiesLoading } = useVarietiesByCustomer(customerId);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    loadFile();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const query = `(SELECT "${t('Date')}", "${t('Ranch')}", "${t('Block')}", "${t(
    'Variety'
  )}", "${t('Cluster')}" FROM (
    SELECT 
      ${formattedDateColumn('report.scan_date')} as '${t('Date')}',
      report.ranch_name as '${t('Ranch')}',
      report.block_name as '${t('Block')}',
      COALESCE(variety.name, report.variety_id) as '${t('Variety')}',
      CAST(round(CAST(report.filtered_cluster_count AS FLOAT), 0) AS INTEGER) as '${t(
        'Cluster'
      )}'
    FROM cluster_report_table report
    LEFT JOIN ${createInlineMappingSubquery(varieties, ['id', 'name'])} variety
    ON variety.id = report.variety_id
    ORDER BY report.scan_date ASC, report.ranch_name ASC, report.block_name ASC
  ))`;

  const exportFileName = createExportFileName('Cluster Count', t);

  const { loadFile, downloadFile, fileReady } = useDownloadCsv(
    query,
    exportFileName,
    shouldUpdate || !isVarietiesLoading,
    true
  );

  return (
    <LoadingSkeleton variant='rounded' loading={loading}>
      <IconButton
        onClick={handleClick}
        aria-label={t('Download CSV')}
        size='small'
        sx={(theme) => ({
          color: theme.vars.palette.text.secondary,
          padding: 2,
          borderRadius: 1,
        })}
      >
        <DownloadIcon
          sx={{
            height: '1rem',
            width: '1rem',
          }}
        />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        elevation={0}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{
          '& .MuiPaper-root': {
            border: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <MenuItem
          disabled={!fileReady}
          onClick={() => {
            handleClose();
            downloadFile();
          }}
        >
          <LoadingSkeleton loading={!fileReady}>
            <Typography variant='body2'>{t('Download .csv')}</Typography>
          </LoadingSkeleton>
        </MenuItem>
      </Menu>
    </LoadingSkeleton>
  );
};
